import React from "react";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import * as companyService from "services/company-service";
import * as userService from "services/identityUser-service";
import * as Yup from "yup";
import { useFormik } from "formik";

interface IProps {
    open: boolean;
    responsible: ResponsiblePaged | null;
    handleClose: () => void;
    refresh: () => void;
    tenantId: string;
}

const EditResponsibleModal = (props: IProps) => {
    const { t } = useTranslation();
    const { open, responsible, handleClose, refresh, tenantId } = props;
    const [companies, _setCompanies] = React.useState<Company[]>([]);
    const [error, setError] = React.useState<{
        error: boolean;
        message: string;
    }>({ error: false, message: "" });
    const [newCompany, setNewCompany] = React.useState<Company[]>([]);
    const [orgInput, setOrgInput] = React.useState<string>("");
    const [listBoxNode, setListBoxNode] =
        React.useState<HTMLUListElement | null>(null);
    const [page, setPage] = React.useState<number>(1);
    const [position, setPosition] = React.useState<number>(0);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [companyErrors, setCompanyErrors] = React.useState<{
        error: boolean;
        message: string[];
    }>({ error: false, message: [] });
    const theme = useTheme();
    const SCHEMA = Yup.object({
        email: Yup.string().email(t("errors.validemail")).required(),
        name: Yup.string().required(t("yup.required")),
        firstName: Yup.string().required(t("yup.required")),
    });
    const formik = useFormik({
        initialValues: {
            email: "",
            name: "",
            firstName: "",
        },
        validationSchema: SCHEMA,
        onSubmit: (values) => {},
    });

    const getCompanies = (nextPage?: number) => {
        companyService.getList(undefined, tenantId).then((res) => {
            setCompanies(res);
        });
    };

    React.useEffect(() => {
        setNewCompany([]);
        setCompanyErrors({ error: false, message: [] });
        if (responsible !== null) {
            setNewCompany(responsible?.companies as Company[]);
            formik.setValues({
                email: responsible.email || "",
                name: responsible.name || "",
                firstName: responsible.firstName || "",
            });
        } else {
            formik.setValues({ email: "", name: "", firstName: "" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [responsible]);

    React.useEffect(() => {
        if (listBoxNode) {
            listBoxNode.scrollTo(0, position);
        }
    }, [position, listBoxNode]);

    const handleInputOrgChange = (event: any, newValue: string) => {
        setOrgInput(newValue);
        setPage(1);
        companyService
            .getAll(1, 10, "", "", newValue, "", tenantId)
            .then((res) => {
                setCompanies(res.results);
            });
    };

    const handleCompanyChange = (event: any, newValue: Company[]) => {
        console.log(newValue);
        setNewCompany(newValue);
    };

    const handleDelete = (): void => {
        if (
            responsible !== null &&
            responsible.id !== null &&
            responsible.id !== undefined
        ) {
            userService
                .deleteResponsible(
                    responsible?.id,
                    responsible.companies.map((c) => c.id),
                    tenantId,
                )
                .then((res) => {
                    refresh();
                    handleClose();
                });
        }
    };

    const handleEdit = (): void => {
        companyService
            .updateResponsible(
                responsible === undefined || responsible === null
                    ? ""
                    : responsible.id,
                newCompany.map((c) => c.id),
                formik.values.email,
                formik.values.name,
                formik.values.firstName,
                tenantId,
            )
            .then((res) => {
                refresh();
                handleClose();
            })
            .catch((err) => {
                console.log(err);
                const {
                    response: {
                        data: { error },
                    },
                } = err;
                const { message } = error;
                const { code } = error;
                const {
                    data: { companies },
                } = error;
                console.log(companies);
                switch (code) {
                    case "406": {
                        setCompanyErrors({
                            error: true,
                            message: (companies as string[]).map(
                                (company: string) => {
                                    return t(
                                        "settings.responsibles.errors.companyInUse",
                                        { company: company },
                                    );
                                },
                            ),
                        });
                        break;
                    }
                    default: {
                        setError({ error: true, message: message });
                        break;
                    }
                }
            });
    };

    const handleScroll = (event: React.UIEvent<HTMLUListElement, UIEvent>) => {
        setListBoxNode(event.currentTarget);

        if (listBoxNode) {
            const x = listBoxNode?.scrollTop + listBoxNode?.clientHeight;

            if (listBoxNode.scrollHeight - x <= 1) {
                setPosition(x);
                const nextPage = page + 1;
                setPage(nextPage);
                getCompanies(nextPage);
            }
        }
    };

    const setCompanies = (companies: Company[]): void => {
        const newCompanies = companies.filter((company, index, self) => {
            const ids = self.map((company) => company.id);
            return index === ids.indexOf(company.id);
        });
        setLoading(false);
        _setCompanies(newCompanies);
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth={"sm"}>
            <DialogTitle>{t("company.edit.title")}</DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        paddingTop: "0.50rem",
                        paddingBottom: "1rem",
                        height: "fit-content",
                    }}
                >
                    <TextField
                        label={t("requestIndividual.step2.responsibleEmail")}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                        error={
                            formik.touched.email && Boolean(formik.errors.email)
                        }
                        helperText={formik.touched.email && formik.errors.email}
                        size="small"
                        id="email"
                        name="email"
                        type="email"
                        sx={{ width: "100%" }}
                    />
                </Box>
                <Box sx={{ paddingBottom: "1rem", height: "fit-content" }}>
                    <TextField
                        label={t(
                            "requestIndividual.step2.responsibleFirstName",
                        )}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.firstName}
                        error={
                            formik.touched.firstName &&
                            Boolean(formik.errors.firstName)
                        }
                        helperText={
                            formik.touched.firstName && formik.errors.firstName
                        }
                        size="small"
                        sx={{ width: "47.5%", marginRight: "5%" }}
                        type="text"
                        id="firstName"
                        name="firstName"
                    />
                    <TextField
                        label={t("requestIndividual.step2.responsibleLastName")}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.name}
                        error={
                            formik.touched.name && Boolean(formik.errors.name)
                        }
                        helperText={formik.touched.name && formik.errors.name}
                        size="small"
                        sx={{ width: "47.5%" }}
                        type="text"
                        id="name"
                        name="name"
                    />
                </Box>
                <Box>
                    {responsible && (
                        <Autocomplete
                            options={companies}
                            disableClearable
                            onOpen={() => {
                                setLoading(true);
                                getCompanies();
                            }}
                            multiple
                            value={newCompany}
                            onInputChange={handleInputOrgChange}
                            inputValue={orgInput}
                            onChange={handleCompanyChange}
                            loading={loading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t(
                                        "settings.responsibles.labels.companyName",
                                    )}
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <React.Fragment>
                                                {loading ? (
                                                    <CircularProgress
                                                        color="inherit"
                                                        size={20}
                                                    />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </React.Fragment>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    <Checkbox
                                        style={{ marginRight: 8 }}
                                        checked={
                                            responsible.companies
                                                .map((c) => c.id)
                                                .includes(option.id) ||
                                            newCompany
                                                .map((c) => c.id)
                                                .includes(option.id)
                                        }
                                    />
                                    {option.name}
                                </li>
                            )}
                            getOptionLabel={(option) => option.name}
                            filterOptions={(x) => x}
                            ListboxProps={{
                                onScroll: handleScroll,
                            }}
                        />
                    )}
                </Box>
                <Box
                    sx={{
                        paddingTop: "0.5rem",
                        height: "fit-content",
                        color: theme.palette.error.main,
                        // display: 'flex',
                        // flexDirection: 'column',
                        // justifyContent: 'center',
                        // alignItems: 'center'
                    }}
                >
                    {companyErrors.message.map((message: string) => (
                        <Box sx={{ paddingBottom: "0.5rem" }}>{message}</Box>
                    ))}
                </Box>
                {error.error && <Box color="error.main">{error.message}</Box>}
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleClose}
                >
                    {t("company.edit.cancel")}
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleDelete}
                >
                    {t("settings.responsibles.create.delete")}
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    onClick={handleEdit}
                >
                    {" "}
                    {t("company.edit.update")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditResponsibleModal;
